import { cloneDeep as _cloneDeep, Dictionary } from 'lodash';
import { Driver, OPTIMIZATION_TYPE, PickupDropoffOption } from '@bringg/types';
import { Notification } from '@bringg/react-components';
import type { GanttTimeLineItem, TimelineItemValuesHistory } from '@bringg-frontend/gantt-timeline';
import type { EtaResponseRouteItem, RunChangedParams } from '@bringg-frontend/eta-calculator';

import { isItemGroupChanged, isRouteAssigned } from '../../components/routes-planner/utils';
import type { RoutesPlannerBreak } from '../../components/routes-planner/routes-planner';
import ColorGenerator from '../../utils/color-generator';
import { NO_ROUTE_COLOR, NO_ROUTE_ID } from '../../stores/routes-planner-store';
import type { NotificationsTranslation } from '../../components/routes-planner/translations';
import type { RoutesPlannerTask, RoutesPlannerRun } from '../../types';
import { etaRoutesConverter } from '../../services/eta/eta-routes-converter';
import { updateTasksAfterItemMoved } from './update-tasks';
import { linkedTaskPriorityChecker } from '../../services/task/linked-task-priority-checker';

export { updateTasksAfterItemMoved, updateRunPriorities } from './update-tasks';

export const getFirstRunsIds = (
	presentedRoutes: RoutesPlannerRun[],
	optimizationType: OPTIMIZATION_TYPE,
	count: number
): number[] => {
	const routes = presentedRoutes.sort((a, b) => {
		return Number(a.id) - Number(b.id);
	});

	const preSelectRuns = new Set<number>();

	for (const route of routes) {
		if (isRouteAssigned(route, optimizationType)) {
			preSelectRuns.add(route.id);
		}

		if (preSelectRuns.size === count) {
			break;
		}
	}

	return [...preSelectRuns];
};

export const calculateChangesAfterItemMoved = (
	item: GanttTimeLineItem,
	valuesHistory: TimelineItemValuesHistory,
	runToTasksInPresentedDate: Dictionary<RoutesPlannerTask[]>,
	runToDriver: Dictionary<Driver>,
	runToBreaks: Dictionary<RoutesPlannerBreak[]>,
	ganttItems: GanttTimeLineItem[],
	notificationsTranslations: NotificationsTranslation
): Dictionary<RoutesPlannerTask[]> => {
	const currentRunId = item.rowId;
	const previousRunId = valuesHistory.previousGroupId;
	const tasksInPreviousRun = runToTasksInPresentedDate[previousRunId].map(task => _cloneDeep(task));
	const tasksInCurrentRun = isItemGroupChanged(item, valuesHistory)
		? (runToTasksInPresentedDate[currentRunId] || []).map(task => _cloneDeep(task))
		: tasksInPreviousRun;

	let linkedItem;

	if (item.customData?.linkedTaskId) {
		linkedItem = ganttItems.find(ganttItem => ganttItem.id === item.customData?.linkedTaskId);
	}

	const tasksChangesByRun = updateTasksAfterItemMoved({
		movedItem: item,
		previousRunId: Number(previousRunId),
		currentRunId: currentRunId,
		tasksInPreviousRun: tasksInPreviousRun,
		tasksInCurrentRun,
		runToDriver,
		runToBreaks,
		linkedItem,
		notificationsTranslations,
		runToTasksInPresentedDate
	});
	return tasksChangesByRun;
};

export const getChangedRoutesData = (
	currentRunId: number,
	previousRunId: number,
	involvedRunsTasks: Dictionary<RoutesPlannerTask[]>,
	runToBreaks: Dictionary<RoutesPlannerBreak[]>,
	routesById: Dictionary<RoutesPlannerRun>,
	routeInfoByIndex: Dictionary<EtaResponseRouteItem>
): RunChangedParams[] => {
	if (currentRunId !== previousRunId) {
		const { [previousRunId]: otherTasksInPreviousRun, [currentRunId]: tasksInNewRun } = involvedRunsTasks;

		return [
			etaRoutesConverter.routeToRunChangedParams(
				routeInfoByIndex[previousRunId] || routesById[previousRunId],
				otherTasksInPreviousRun ?? [],
				runToBreaks[previousRunId]
			),
			etaRoutesConverter.routeToRunChangedParams(
				routeInfoByIndex[currentRunId] || routesById[currentRunId],
				tasksInNewRun,
				runToBreaks[currentRunId]
			)
		];
	} else {
		const { [currentRunId]: tasksInCurrentRun } = involvedRunsTasks;
		return [
			etaRoutesConverter.routeToRunChangedParams(
				routeInfoByIndex[currentRunId] || routesById[currentRunId],
				tasksInCurrentRun,
				runToBreaks[currentRunId]
			)
		];
	}
};

export const updateTaskAfterDrag = (
	task: RoutesPlannerTask,
	relatedGanttItem: GanttTimeLineItem,
	driver: Driver,
	runToTasksInPresentedDate: { [runId: number]: RoutesPlannerTask[] }
): RoutesPlannerTask => {
	task.run_id = relatedGanttItem.rowId;
	task.user_id = driver?.id || null;
	task.vehicle_id = runToTasksInPresentedDate[task.run_id][0].vehicle_id;
	task.user = driver || null;

	if (task.way_points && task.way_points[0]) {
		task.way_points[0].eta = relatedGanttItem.start;
		task.way_points[0].scheduled_at = relatedGanttItem.start;
		task.way_points[0].etl = relatedGanttItem.end;
	}

	return task;
};

export const addLinkedTaskToTasksList = (
	linkedTask: RoutesPlannerTask,
	tasksList: (RoutesPlannerTask | RoutesPlannerBreak)[]
) => {
	const isPickup = linkedTask.way_points[0].pickup_dropoff_option === PickupDropoffOption.PICKUP;
	const linkedTaskIndex = tasksList.findIndex(task => task.id === linkedTask.linked_task_id);
	const beforeDropOffTask = linkedTaskIndex;
	const afterPickupTask = linkedTaskIndex + 1;

	tasksList.splice(isPickup ? beforeDropOffTask : afterPickupTask, 0, linkedTask);
};

export const getResequenceRoutesData = ({
	tasksToMove,
	linkedTasksThatNotSelected,
	currentRunId,
	currentPriority,
	runToTasksInPresentedDate,
	runToBreaks,
	runToDriver,
	notificationsTranslations,
	routesById,
	routeInfoByIndex
}: {
	tasksToMove: RoutesPlannerTask[];
	linkedTasksThatNotSelected: RoutesPlannerTask[];
	currentRunId: number;
	currentPriority: number;
	runToTasksInPresentedDate: { [runId: number]: RoutesPlannerTask[] };
	runToBreaks: { [runId: number]: RoutesPlannerBreak[] };
	runToDriver: { [runId: number]: Driver };
	notificationsTranslations: NotificationsTranslation;
	routesById: { [runId: number]: RoutesPlannerRun };
	routeInfoByIndex: { [runId: number]: EtaResponseRouteItem };
}): RunChangedParams[] => {
	const selectedTasksAndLinkedTasksUniqueIds = new Set([
		...tasksToMove.map(task => task.id),
		...linkedTasksThatNotSelected.map(linkedTask => linkedTask.id)
	]);

	if (tasksToMove[0].run_id !== currentRunId) {
		const previousRunId = tasksToMove[0].run_id;

		const tasksInPreviousRunWithoutSelectedTasks = runToTasksInPresentedDate[previousRunId].filter(
			task => !selectedTasksAndLinkedTasksUniqueIds.has(task.id)
		);
		const breaksInPreviousRun = runToBreaks[previousRunId] || [];

		const tasksAndBreaksInPreviousRun = sortTasksAndBreaksByPriority([
			...tasksInPreviousRunWithoutSelectedTasks,
			...breaksInPreviousRun
		]);

		const [sortedTasksInPreviousRun, sortedBreaksInPreviousRun] = splitAndPrioritySet(tasksAndBreaksInPreviousRun);

		const tasks = runToTasksInPresentedDate[currentRunId];
		const runBreaks = runToBreaks[currentRunId] || [];

		tasksToMove.forEach(task => {
			task.run_id = currentRunId;
			task.user_id = runToDriver[currentRunId]?.id;
			task.vehicle_id = tasks[0].vehicle_id;
		});

		const tasksAndBreaks = sortTasksAndBreaksByPriority([...tasks, ...runBreaks]);

		tasksAndBreaks.splice(currentPriority - 1, 0, ...(tasksToMove as RoutesPlannerTask[]));

		if (linkedTasksThatNotSelected.length) {
			linkedTasksThatNotSelected.forEach(linkedTask => {
				linkedTask.run_id = currentRunId;
				linkedTask.user_id = runToDriver[currentRunId]?.id;
				linkedTask.vehicle_id = tasks[0].vehicle_id;
				addLinkedTaskToTasksList(linkedTask, tasksAndBreaks);
			});
			Notification.info(notificationsTranslations.moveLinkedOrder);
		}
		const [sortedTasks, sortedBreaks] = splitAndPrioritySet(tasksAndBreaks);

		return [
			etaRoutesConverter.routeToRunChangedParams(
				routeInfoByIndex[previousRunId] || routesById[previousRunId],
				sortedTasksInPreviousRun,
				sortedBreaksInPreviousRun
			),
			etaRoutesConverter.routeToRunChangedParams(
				routeInfoByIndex[currentRunId] || routesById[currentRunId],
				sortedTasks,
				sortedBreaks
			)
		];
	} else {
		const selectedTasksIds = tasksToMove.map(task => task.id);
		const linkedTasksNotValid: RoutesPlannerTask[] = [];
		const tasksWithoutSelectedTasks = runToTasksInPresentedDate[currentRunId].filter(
			task => !selectedTasksIds.includes(task.id)
		);
		const tasksWithValidateLinkedTasks = tasksWithoutSelectedTasks.filter(task =>
			linkedTaskPriorityChecker.isPriorityValid(task, selectedTasksIds, currentPriority, linkedTasksNotValid)
		);
		const runBreaks = runToBreaks[currentRunId] || [];

		const tasksAndBreaks = sortTasksAndBreaksByPriority([...tasksWithValidateLinkedTasks, ...runBreaks]);
		tasksAndBreaks.splice(currentPriority - 1, 0, ...tasksToMove);

		if (linkedTasksNotValid.length) {
			linkedTasksNotValid.forEach(linkedTask => addLinkedTaskToTasksList(linkedTask, tasksAndBreaks));
			Notification.info(notificationsTranslations.moveLinkedOrder);
		}

		const [sortedTasks, sortedBreaks] = splitAndPrioritySet(tasksAndBreaks.slice());

		return [
			etaRoutesConverter.routeToRunChangedParams(
				routeInfoByIndex[currentRunId] || routesById[currentRunId],
				sortedTasks,
				sortedBreaks
			)
		];
	}
};

export const getDateToSort = (item: RoutesPlannerTask | RoutesPlannerBreak) =>
	Date.parse(
		(item as RoutesPlannerTask).way_points
			? (item as RoutesPlannerTask).way_points[0].scheduled_at
			: (item as RoutesPlannerBreak).estimated_start_time
	);

export const sortTasksAndBreaksByPriority = (tasksAndBreaks: (RoutesPlannerTask | RoutesPlannerBreak)[]) => {
	return tasksAndBreaks.sort((a, b) => {
		const priorityA = (a as RoutesPlannerTask).priority || (a as RoutesPlannerBreak).position;
		const priorityB = (b as RoutesPlannerTask).priority || (b as RoutesPlannerBreak).position;

		return priorityA - priorityB;
	});
};

export const sortTasksByRunIdAndPriority = (tasks: RoutesPlannerTask[]) => {
	return tasks.sort((a, b) => {
		if (a.run_id !== b.run_id) {
			return b.run_id - a.run_id;
		}
		return a.priority - b.priority;
	});
};

export const splitAndPrioritySet = (
	tasksAndBreaks: (RoutesPlannerTask | RoutesPlannerBreak)[]
): [RoutesPlannerTask[], RoutesPlannerBreak[]] =>
	tasksAndBreaks.reduce(
		([_tasks, _breaks], item, i) => {
			if ((item as RoutesPlannerTask).way_points) {
				const task = { ...item } as RoutesPlannerTask;
				task.priority = i + 1;

				_tasks.push(task);
			} else {
				const aBreak: RoutesPlannerBreak = { ...item } as RoutesPlannerBreak;
				aBreak.position = i + 1;

				_breaks.push(aBreak);
			}

			return [_tasks, _breaks];
		},
		[[] as RoutesPlannerTask[], [] as RoutesPlannerBreak[]]
	);

export const getInitialRoutesColors = (routesById: Dictionary<RoutesPlannerRun>) => {
	const colorsMap = new Map(
		Object.keys(routesById).map(routeId => {
			return [Number(routeId), ColorGenerator.getColor()];
		})
	);

	colorsMap.set(NO_ROUTE_ID, NO_ROUTE_COLOR);

	return colorsMap;
};

export const getTaskDuration = (eta, etl, etos?) => {
	let delta;
	if (!eta || !etl) {
		delta = etos ? etos * 1000 : 0;
	} else {
		delta = new Date(etl).getTime() - new Date(eta).getTime();
	}
	return delta;
};
